import { createGenericCRUD, updateListCache } from './commonCrud';
import useSWR, { mutate } from 'swr';
import { fetcher, fetcherWithMethod } from 'utils/axios';
import { handleApiError, showSuccess } from 'utils/errorHandler';
import { useMemo } from 'react';
import { invalidateUserPermissions } from './authUser';
import { Role, RolePermissionItem, RolePermissionsResponse, UpdateRolePermissionsPayload } from 'types/userrole';
import { DuplicateResponse, CrudEndpoints } from 'types/common';

export const endpoints: CrudEndpoints = {
  key: 'UserManagement/UserRole',
  getList: '/GetList',
  getListById: '/GetList/{ID}',
  getModal: '/',
  getLookupList: '/GetLookupList',
  insert: '/',
  update: '/',
  delete: '',
  getlookupPost: true
};

const roleCRUD = createGenericCRUD<Role>(endpoints);

export const useCheckDuplicateRoleName = async (roleName: string, excludeID?: number): Promise<boolean> => {
  if (!roleName) return false;
  const endpoint = `${endpoints.key}/CheckDuplicateRoleName?RoleName=${encodeURIComponent(roleName)}${excludeID ? `&ExcludeID=${excludeID}` : ''}`

  try {
    const res = await fetcherWithMethod('GET', [endpoint, { method: 'GET' }]);
    return res as boolean;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const useGetRolePermissions = (roleID?: number) => {
  const endpoint = roleID ? `${endpoints.key}/GetPermissions/${roleID}` : null;

  const { data, error, isLoading, mutate } = useSWR<RolePermissionsResponse>(
    endpoint ? [endpoint, { method: 'GET' }] : null,
    (args: [string, any]) => fetcherWithMethod('GET', args),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: handleApiError
    }
  );
  const groupedData = useMemo(() => {
    if (!data) return {};
    return data.reduce(
      (acc, item) => {
        if (!acc[item.moduleName]) acc[item.moduleName] = [];
        acc[item.moduleName].push(item);
        return acc;
      },
      {} as Record<string, RolePermissionItem[]>
    );
  }, [data]);

  return {
    permissions: data ?? [],
    groupedPermissions: groupedData,
    error,
    isLoading,
    mutate,
    hasError: !!error,
    retry: () => mutate()
  };
};

export const useUpdateRolePermissions = async (payload: UpdateRolePermissionsPayload) => {
  try {
    const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdatePermissions`, { data: payload }]);

    const key = [`${endpoints.key}/GetPermissions/${payload.userRoleID}`, { method: 'GET' }];
    mutate(key);

    const listKey = `${endpoints.key}${endpoints.getList}`;
    const getByIdEndpoint = endpoints.getListById.replace('{ID}', payload.userRoleID.toString());
    const fullEndpoint = `${endpoints.key}${getByIdEndpoint}`;

    const fetchedItem = await fetcher(fullEndpoint);
    if (fetchedItem) {
      updateListCache(fetchedItem, listKey);
    }
    // Invalidate logged-in user's permissions so UI (menu) re-filters immediately
    invalidateUserPermissions();
    showSuccess('Updated successfully');
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const useGetRoleListWithFilters = () => {
  const endpoint = `${endpoints.key}${endpoints.getList}`;

  const { data, error, isLoading, isValidating } = useSWR(endpoint, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return {
    data,
    isLoading: !data && !error,
    error,
    isValidating,
    mutate
  };
};

export const useUpdateActiveStatus = () => {
  const updateActiveStatus = async (data: { updatedOn: string | null; userRoleID: number; active: boolean }) => {
    try {
      const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateActiveStatus`, { data }]);

      const listKey = `${endpoints.key}${endpoints.getList}`;
      const getByIdEndpoint = endpoints.getListById.replace('{ID}', data.userRoleID.toString());
      const fullEndpoint = `${endpoints.key}${getByIdEndpoint}`;

      const fetchedItem = await fetcher(fullEndpoint);
      if (fetchedItem) {
        updateListCache(fetchedItem, listKey);
      }

      showSuccess('Status updated successfully');
      return response;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };
  return { updateActiveStatus };
};

export const {
  useGetList: useGetRoleList,
  useGetListByID: useGetRoleListByID,
  useGetModal: useGetRoleModal,
  useGetLookupList: useGetRoleLookupList,
  useInsertRecord: useInsertRoleRecord,
  useUpdateRecord: useUpdateRoleRecord,
  useDeleteRecord: useDeleteRoleRecord
} = roleCRUD;
